import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  TrendingUp, 
  CreditCard, 
  Calendar,
  Search,
  ArrowUpRight,
  ArrowDownRight,
  Filter,
  Download,
  Copy,
  Check,
  ShieldCheck,
  LogOut
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import toast from 'react-hot-toast';
import { partnerAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import LoadingSpinner from '../components/common/LoadingSpinner';

const PartnerDashboard = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const res = await partnerAPI.getDashboard();
      if (res.data.success) {
        setStats(res.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    if (stats?.referralCode) {
      navigator.clipboard.writeText(stats.referralCode);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const logoutPartner = () => {
    localStorage.removeItem('connectionunlimited-token');
    window.location.href = '/partner/login';
    toast.success('Logged out successfully');
  };

  if (loading) return <div className="h-screen flex items-center justify-center"><LoadingSpinner /></div>;

  const { stats: mainStats, recentOrders, monthlyStats, referralCode } = stats || {};

  const filteredOrders = recentOrders?.filter(order => 
    order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (order.customerInfo?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white dark:bg-black p-4 md:p-8 font-sans transition-colors duration-300">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center space-x-2">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Partner Dashboard</h1>
              {user?.isVerified && (
                <div className="flex items-center space-x-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold animate-pulse">
                  <ShieldCheck size={14} />
                  <span>VERIFIED PARTNER</span>
                </div>
              )}
            </div>
            <p className="text-gray-500 dark:text-gray-400 mt-1">Welcome back, {user?.name || 'Partner'}. Here's what's happening with your referrals.</p>
          </div>
          
          <div className="flex items-center space-x-3 bg-white dark:bg-gray-900 p-2 rounded-2xl shadow-sm border border-gray-100 dark:border-black">
            <div className="px-4 py-2 bg-blue-50 text-blue-700 rounded-xl font-mono font-bold text-lg">
              {referralCode || 'NOT_FOUND'}
            </div>
            <button 
              onClick={copyReferralCode}
              className="p-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all active:scale-95 shadow-md shadow-blue-200"
            >
              {copied ? <Check size={20} /> : <Copy size={20} />}
            </button>
            <button 
              onClick={logoutPartner}
              className="p-3 bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/20 transition-all active:scale-95 border border-red-100 dark:border-red-900/30"
              title="Logout"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { label: 'Total Referrals', value: mainStats?.totalReferrals || 0, icon: Users, color: 'bg-blue-500', trend: '+12%' },
            { label: 'Total Sales', value: `₹${mainStats?.totalSales || 0}`, icon: CreditCard, color: 'bg-green-500', trend: '+8%' },
            { label: 'Earned Commission', value: `₹${mainStats?.totalEarned || 0}`, icon: TrendingUp, color: 'bg-purple-500', trend: '+15%' },
            { label: 'Paid Commission', value: `₹${mainStats?.totalPaid || 0}`, icon: Check, color: 'bg-orange-500', trend: '+5%' },
          ].map((item, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-white dark:bg-gray-900 p-6 rounded-3xl shadow-sm border border-gray-100 dark:border-black relative overflow-hidden group hover:shadow-md transition-all"
            >
              <div className={`absolute top-0 right-0 w-24 h-24 ${item.color} opacity-[0.03] rounded-bl-full group-hover:opacity-[0.06] transition-all`} />
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-2xl ${item.color} text-white`}>
                  <item.icon size={24} />
                </div>
                <div className={`flex items-center text-xs font-bold ${item.trend.startsWith('+') ? 'text-green-600' : 'text-red-600'} bg-opacity-10 px-2 py-1 rounded-lg`}>
                   {item.trend.startsWith('+') ? <ArrowUpRight size={14} className="mr-1" /> : <ArrowDownRight size={14} className="mr-1" />}
                   {item.trend}
                </div>
              </div>
              <h3 className="text-gray-500 dark:text-gray-400 text-sm font-medium">{item.label}</h3>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{item.value}</p>
            </motion.div>
          ))}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white dark:bg-gray-900 p-8 rounded-3xl shadow-sm border border-gray-100 dark:border-black">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Referral Performance</h2>
              <div className="flex space-x-2">
                 <button className="px-3 py-1 text-xs font-bold bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg">Last 6 Months</button>
                 <button className="px-3 py-1 text-xs font-bold text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">Yearly</button>
              </div>
            </div>
            <div className="h-80 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthlyStats || []}>
                  <defs>
                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? "#374151" : "#f3f4f6"} />
                  <XAxis dataKey="_id.month" axisLine={false} tickLine={false} tick={{fill: '#9ca3af', fontSize: 12}} />
                  <YAxis axisLine={false} tickLine={false} tick={{fill: '#9ca3af', fontSize: 12}} />
                  <Tooltip 
                    contentStyle={{
                      borderRadius: '16px', 
                      border: 'none', 
                      boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                      backgroundColor: isDark ? '#1f2937' : '#ffffff',
                      color: isDark ? '#ffffff' : '#000000'
                    }}
                  />
                  <Area type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorCount)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 p-8 rounded-3xl shadow-sm border border-gray-100 dark:border-black">
             <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Sales Distribution</h2>
             <div className="h-80 w-full">
               <ResponsiveContainer width="100%" height="100%">
                 <BarChart data={monthlyStats || []}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? "#374151" : "#f3f4f6"} />
                    <XAxis dataKey="_id.month" axisLine={false} tickLine={false} tick={{fill: '#9ca3af', fontSize: 12}} />
                    <YAxis axisLine={false} tickLine={false} tick={{fill: '#9ca3af', fontSize: 12}} />
                    <Tooltip 
                      cursor={{fill: isDark ? '#374151' : '#f9fafb'}} 
                      contentStyle={{
                        borderRadius: '16px', 
                        border: 'none', 
                        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                        backgroundColor: isDark ? '#1f2937' : '#ffffff',
                        color: isDark ? '#ffffff' : '#000000'
                      }} 
                    />
                    <Bar dataKey="amount" fill="#10b981" radius={[10, 10, 0, 0]} barSize={30} />
                 </BarChart>
               </ResponsiveContainer>
             </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-sm border border-gray-100 dark:border-black overflow-hidden">
          <div className="p-8 border-b border-gray-50 dark:border-black flex flex-col md:flex-row md:items-center justify-between gap-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Referral History</h2>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input 
                  type="text" 
                  placeholder="Search orders..." 
                  className="pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-800 border-none rounded-xl text-sm focus:ring-2 focus:ring-blue-500 w-full md:w-64 dark:text-white"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button className="p-2 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <Filter size={20} />
              </button>
              <button className="p-2 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <Download size={20} />
              </button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50/50 dark:bg-gray-800/50">
                  <th className="px-8 py-4 text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Order ID</th>
                  <th className="px-8 py-4 text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Customer</th>
                  <th className="px-8 py-4 text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Date</th>
                   <th className="px-8 py-4 text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-8 py-4 text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-8 py-4 text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Payment</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                {filteredOrders?.map((order, i) => (
                  <tr key={i} className="hover:bg-gray-50/30 dark:hover:bg-gray-800/30 transition-colors group">
                    <td className="px-8 py-4">
                      <span className="text-sm font-bold text-gray-900 dark:text-white">{order.orderNumber}</span>
                    </td>
                    <td className="px-8 py-4">
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold text-xs mr-3">
                          {order.customerInfo?.name?.charAt(0) || 'U'}
                        </div>
                        <span className="text-sm text-gray-600 dark:text-gray-300">{order.customerInfo?.name || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="px-8 py-4">
                      <span className="text-sm text-gray-500 dark:text-gray-400">{new Date(order.createdAt).toLocaleDateString()}</span>
                    </td>
                    <td className="px-8 py-4">
                      <span className="text-sm font-bold text-gray-900 dark:text-white">₹{order.totalAmount * 0.2} </span>
                    </td>
                    <td className="px-8 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                        order.status === 'delivered' ? 'bg-green-100 text-green-700' : 
                        order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                       </span>
                    </td>
                    <td className="px-8 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                        order.referralCommissionStatus === 'paid' ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400' : 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400'
                      }`}>
                        {order.referralCommissionStatus === 'paid' ? 'Paid' : 'Unpaid'}
                      </span>
                    </td>
                  </tr>
                ))}
                {(!filteredOrders || filteredOrders.length === 0) && (
                  <tr>
                    <td colSpan="5" className="px-8 py-12 text-center text-gray-400">
                      No referrals found matching your criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default PartnerDashboard;
